/* eslint-disable no-nested-ternary */
import React from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import '../textInput/text-input.css';
import './iconDropDown.css';

const toControlHeight = (controlHeight) => (
    typeof controlHeight === 'number' ? `${controlHeight}px` : controlHeight
);

/**
 * Matches device Configuration → General: same outer width as `.asset-name-input` (32rem),
 * same Plain TextInput DOM stack, react-select fills the shell (see Configuration.js DropDownList usage).
 */
const customStyles = (controlHeightPx) => ({
    container: (base) => ({
        ...base,
        width: '100%',
        flex: '1 1 auto',
        minWidth: 0,
    }),
    control: (base, state) => ({
        ...base,
        boxSizing: 'border-box',
        width: '100%',
        minWidth: 0,
        height: controlHeightPx,
        minHeight: controlHeightPx,
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: state.isDisabled ? '#F9FAFB' : 'transparent',
        fontFamily: 'Inter',
        /* Selected value + menu options use 0.875rem / 400 — match supporting hint text */
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
        alignItems: 'center',
        padding: 0,
        cursor: state.isDisabled ? 'not-allowed' : 'default',
        opacity: state.isDisabled ? 0.7 : 1,
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0.625rem 0.875rem',
        gap: '0.5rem',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        paddingRight: '0.5rem',
        alignItems: 'center',
    }),
    dropdownIndicator: (base, state) => ({
        ...base,
        padding: '0 0.25rem',
        color: state.isDisabled ? '#D0D5DD' : '#667085',
    }),
    singleValue: (base, state) => ({
        ...base,
        fontFamily: 'Inter',
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
        margin: 0,
        color: state.isDisabled ? '#98A2B3' : '#475467',
    }),
    /* Match `.configuration-supporting-text` (e.g. Description hint under Vehicle Type) */
    placeholder: (base) => ({
        ...base,
        fontFamily: 'Inter',
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
        margin: 0,
        color: '#475467',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        position: 'absolute',
    }),
    menuPortal: (base) => ({
        ...base,
        fontFamily: 'Inter',
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
    }),
    option: (provided, state) => ({
        ...provided,
        fontFamily: 'Inter',
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
        color: '#475467',
        backgroundColor: state.isSelected ? '#F9FAFB' : state.isFocused ? '#F9FAFB' : 'transparent',
        cursor: 'pointer',
        ':active': '#F9FAFB',
    }),
    input: (baseStyles) => ({
        ...baseStyles,
        color: 'transparent',
    }),
});

export default function DropDownList(props) {
    const {
        selectedValue,
        setSelectedValue,
        options,
        placeholder,
        /** Same as `.asset-name-input` in Configuration.css (Configuration passes no width → use this default) */
        width = '32rem',
        /** `.asset-name-input` uses height 2.75rem (44px) */
        controlHeight = 44,
        maxWidth,
        menuPortal = false,
        isDisabled: isDisabledProp = false,
        disabled = false,
    } = props;

    const isDisabled = isDisabledProp || disabled;

    const animatedComponents = makeAnimated();

    const handleChange = (e) => {
        if (isDisabled) return;
        setSelectedValue(e);
    };

    const controlHeightPx = toControlHeight(controlHeight);

    const fillsParentWidth = typeof width === 'string' && width.trim().endsWith('%');

    const rootStyle = {
        width,
        boxSizing: 'border-box',
        flexShrink: fillsParentWidth ? 1 : 0,
        minWidth: 0,
        ...(fillsParentWidth ? { alignSelf: 'stretch' } : {}),
        ...(maxWidth != null && maxWidth !== '' ? { maxWidth } : {}),
    };

    return (
        <div
          className={`text-input-container icon-dropdown-root${fillsParentWidth ? ' icon-dropdown-root--fill' : ''}`}
          style={rootStyle}
        >
            <div className="text-input" style={{ width: '100%', minWidth: 0 }}>
                <div
                  className={`text-input-box icon-dropdown-select-shell${isDisabled ? ' icon-dropdown-select-shell--disabled' : ''}`}
                  style={{
                      width: '100%',
                      border: '0.063rem solid #D0D5DD',
                      boxSizing: 'border-box',
                  }}
                >
                    <Select
                      placeholder={placeholder}
                      options={options}
                      value={selectedValue}
                      styles={customStyles(controlHeightPx)}
                      onChange={handleChange}
                      menuPortalTarget={menuPortal ? document.body : undefined}
                      menuPosition={menuPortal ? 'fixed' : 'absolute'}
                      isDisabled={isDisabled}
                      formatGroupLabel={(group) => (
                          <div className="icon-dropdown-group-label">
                              {group.label}
                          </div>
                      )}
                      getOptionLabel={(e) => (
                          <div className="menu-container">
                              {e.icon && (
                                  <div className="menu-icon">
                                      {e.icon}
                                  </div>
                              )}
                              <div className="menu-text">
                                  {e.label}
                              </div>
                              <div className="menu-subtitle">
                                  {e.subtitle}
                              </div>
                          </div>
                      )}
                      components={{
                          IndicatorSeparator: () => null,
                          animatedComponents,
                      }}
                      isOptionSelected={(option) => (selectedValue ? option.value === selectedValue.value : false)}
                    />
                </div>
            </div>
        </div>
    );
}
